// Dot & Moon storefront client. Upload to the theme as assets/dotandmoon.js and include with:
//   <script src="{{ 'dotandmoon.js' | asset_url }}" defer></script>
//
// Several books at once (the "create your book" flow: every child x every chosen book is its own personalised book):
//   const book = await DotMoon.createBook({ handle: "superhero-girl", cover: "hardcover", key: "child1" }); // resumes per key
//   await book.update({ child_name: "Mila", child_age: 4, child_pronoun: "she", dedication: "..." });
//   await book.uploadPhoto(file);                    // skipped when this exact file is already uploaded for this book
//   await book.uploadPhoto(secondFile, { extra: true }); // optional: up to 2 more photos of the same child
//   const { pages, teaser } = await book.preview({ email, consent, onProgress }); // background, ~2 min; pages arrive one by one
//     A preview already made from the same details and photo comes straight back (also after leaving the page); again: true
//     draws a new one (max 5 per book). Every update has progress { elapsed_ms, expected_ms, sheet_done, done, total }
//     while drawing; DotMoon.percent(res) turns it into 0-100.
//   book.hasPhoto: a photo is already uploaded for this book (so after a page reload the file isn't needed again).
//   await DotMoon.addBooksToCart([{ book, variantId, quantity: 1, properties: { Child: "Mila" } }]);
//
// One book on a product page (older form, still supported):
//   await DotMoon.startDraft("superhero-girl", "hardcover"); await DotMoon.update({...}); await DotMoon.uploadPhoto(file);
//   const { pages, teaser } = await DotMoon.preview("parent@example.com", true); await DotMoon.addToCart(variantId);
//   await DotMoon.joinList("parent@example.com", { consent: true, source: "popup" });
//
// Preview pages: { page_number, kind: "art", url } (drawn), { kind: "pending" } (still drawing),
//                { kind: "text", url | null, text } (dedication / activity page: the page art plus its text).
// Showing the pages like the printed book: DotMoon.tileHtml(page, res, alt) -> one <li> per page (the art plus the book's
// text laid out exactly as print/toolkit/bookkit.py does, in Nunito), DotMoon.morph(container, html) updates a list without
// reloading pages that didn't change (every poll has new signed URLs), DotMoon.stageText(res, name) -> "Painting page 2 of 4".
// Email sign-up: DotMoon.subscribe(email, { consent, source }) keeps the email in our list (leads) and, with consent, adds the
// person to Shopify Customers subscribed to email marketing (through the store's own newsletter form, tag "preview").
// Errors carry .code: preview_limit_book | preview_limit_email | preview_limit_device | preview_limit_daily | preview_failed.
(function () {
  const API = "https://gfztxlaqecpgohwwuwkg.supabase.co/functions/v1";
  const KEY = (k) => `dotandmoon:draft:${k}`;
  const POLL_MS = 3000;
  const PREVIEW_TIMEOUT_MS = 10 * 60 * 1000;

  function load(k) {
    try { return JSON.parse(localStorage.getItem(KEY(k)) || "null"); } catch { return null; }
  }
  function save(k, d) {
    try { localStorage.setItem(KEY(k), JSON.stringify(d)); } catch { /* private mode */ }
  }
  function forget(k) {
    try { localStorage.removeItem(KEY(k)); } catch { /* ignore */ }
  }

  async function call(path, token, opts = {}) {
    const res = await fetch(API + path, {
      ...opts,
      headers: { "Content-Type": "application/json", ...(token ? { "x-book-token": token } : {}), ...(opts.headers || {}) },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(body.detail || body.error || res.statusText);
      err.code = body.error;
      throw err;
    }
    return body;
  }

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const fileId = (f) => `${f.name}|${f.size}|${f.lastModified}`;

  // One personalised book (a draft until it is paid). key: what the draft is remembered by in this browser.
  function Book(d) {
    const self = {
      id: d.id, token: d.token, handle: d.handle, key: d.key,
      get hasPhoto() { return !!d.photo; },
      async update(fields) {
        const { book } = await call(`/books/${d.id}`, d.token, { method: "PATCH", body: JSON.stringify(fields) });
        return book;
      },
      async uploadPhoto(file, { extra = false } = {}) {
        if (file.size > 10 * 1024 * 1024) throw new Error("Photo must be under 10 MB");
        if (!extra && d.photo === fileId(file)) return; // already uploaded for this book
        const { upload_url } = await call(`/books/${d.id}/photo`, d.token, {
          method: "POST", body: JSON.stringify({ content_type: file.type || "image/jpeg", extra }),
        });
        const res = await fetch(upload_url, { method: "PUT", headers: { "Content-Type": file.type || "image/jpeg" }, body: file });
        if (!res.ok) throw new Error("Photo upload failed");
        if (!extra) { d.photo = fileId(file); save(d.key, d); }
      },
      getPreview() { return call(`/books/${d.id}/preview`, d.token); },
      // Starts the preview (drawn in the background) and waits until it is ready; onProgress gets every update, so pages
      // can be shown as soon as each one is drawn. An already running preview is simply waited for.
      async preview({ email = "", consent = false, again = false, onProgress } = {}) {
        let res;
        try {
          res = await call(`/books/${d.id}/preview`, d.token, { method: "POST", body: JSON.stringify({ email, consent, again }) });
        } catch (e) {
          // a limit or a bad request is final; a server or network hiccup is not: the preview may well be drawing already
          // (end-to-end test, 15 Oct: one storage error answered the start with a 500 while the pages were being drawn)
          if (e.code !== "preview_running" && e.code !== "server_error" && e.code !== undefined) throw e;
          res = await self.getPreview();
        }
        const until = Date.now() + PREVIEW_TIMEOUT_MS;
        let misses = 0;
        while (res.status === "running" && Date.now() < until) {
          if (onProgress) onProgress(res);
          await sleep(POLL_MS);
          try {
            res = await self.getPreview();
            misses = 0;
          } catch (e) {
            if (++misses >= 5) throw e; // keep the last answer and ask again, unless the server stays down (~15 s)
          }
        }
        if (res.status !== "ready") throw Object.assign(new Error("preview failed"), { code: "preview_failed" });
        if (res.cover_url) { d.cover = res.cover_url; save(d.key, d); } // for the cart line's _cover
        if (onProgress) onProgress(res);
        return res;
      },
      cartLine(variantId, quantity = 1, properties = {}) {
        // Underscore-prefixed properties are hidden from customers at checkout.
        // _cover: the drawn cover (link valid 60 days) for the checkout and Klaviyo's cart / order emails (team, 14 Oct)
        return { id: variantId, quantity, properties: { ...(d.cover ? { _cover: d.cover } : {}), ...properties, _book_id: d.id,
          _book_token: d.token } };
      },
      forget() { forget(d.key); },
    };
    return self;
  }

  // The link in the "Preview Ready" email (?book=<id>&t=<token>) opens exactly that book, on any device (team, 17 Oct: it
  // opened the browser's latest draft of the same book, another child). Used once, by the first createBook for its book.
  let fromLink = (() => {
    try { const p = new URLSearchParams(location.search); return p.get("book") && p.get("t") ? { id: p.get("book"), token: p.get("t") } : null; }
    catch { return null; }
  })();

  // Resumes the draft remembered under key (if it is still a draft for the same book), else starts a new one.
  async function createBook({ handle, cover = "softcover", key = handle }) {
    if (fromLink) {
      const l = fromLink;
      try {
        const { book } = await call(`/books/${l.id}`, l.token);
        if (book.status === "draft" && book.product?.handle === handle) {
          fromLink = null;
          const d = { id: l.id, token: l.token, handle, key };
          save(key, d);
          return Book(d);
        }
      } catch { /* the link's book was ordered or is gone: carry on as usual */ fromLink = null; }
    }
    const saved = load(key);
    if (saved && saved.handle === handle) {
      try {
        const { book } = await call(`/books/${saved.id}`, saved.token);
        if (book.status === "draft") return Book(saved);
      } catch { /* expired or already ordered: start fresh */ }
    }
    forget(key);
    const { id, token } = await call("/books", null, { method: "POST", body: JSON.stringify({ product_handle: handle, cover }) });
    const d = { id, token, handle, key };
    save(key, d);
    return Book(d);
  }

  // Time left for one running preview, from the stage it is in (measured on the store's previews, Oct 2026: the character
  // sheet takes ~35 s, then the pages, drawn side by side, ~65 s more; a cover whose name needs fixing can take longer).
  // extraMs: time since this answer came, so the countdown can tick every second between polls. Once a stage runs over,
  // the estimate shrinks slowly towards a few seconds instead of standing still or jumping.
  const SHEET_MS = 35000, PAGES_MS = 65000;
  const overrun = (left, floor) => (left > floor ? left : floor * Math.exp((left - floor) / (floor * 3)));
  function remainingMs(res, extraMs = 0) {
    if (!res || res.status !== "running") return 0;
    const p = res.progress;
    if (!p) return SHEET_MS + PAGES_MS;
    const elapsed = p.elapsed_ms + extraMs;
    if (!p.sheet_done) return overrun(SHEET_MS - elapsed, 8000) + PAGES_MS;
    const left = PAGES_MS - (elapsed - (p.sheet_ms ?? SHEET_MS));
    return overrun(left, p.total && p.done >= p.total - 1 ? 6000 : 12000);
  }

  // 0-100 for a progress bar, from the same estimate as the countdown: time spent / (time spent + time left). Stays under
  // 100 until the preview is ready.
  function percent(res, extraMs = 0) {
    if (!res) return 0;
    if (res.status === "ready") return 100;
    const p = res.progress;
    if (!p) return 0;
    const spent = p.elapsed_ms + extraMs;
    return Math.round(Math.min(97, (100 * spent) / (spent + remainingMs(res, extraMs))));
  }

  // ---- pages laid out like the printed book ----
  // The widest a line may be (1254 px page): the print's safe area (bookkit.py safe_w), 64 px from each edge less 10 px.
  const SAFE_W = 1116;
  let measureCtx = null;
  // 0..1: how much a block of lines must shrink so its widest line fits maxw (measured in Nunito; the 2% covers a fallback
  // font while Nunito is still loading)
  function fitSize(lines, sizePx, weight, maxw) {
    try {
      measureCtx = measureCtx || document.createElement("canvas").getContext("2d");
      measureCtx.font = `${weight} ${sizePx}px Nunito, system-ui, sans-serif`;
      const w = Math.max(...lines.map((l) => measureCtx.measureText(String(l)).width)) * 1.02;
      return w > maxw ? maxw / w : 1;
    } catch { return 1; }
  }
  // Positions and sizes are the print toolkit's (a 1254 px page), as percentages of the tile: 46 px type = 46 / 1254 of the width.
  const U = (px) => (px / 12.54).toFixed(3);
  const esc = (t) => String(t == null ? "" : t).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);
  const rgb = (a, d) => (Array.isArray(a) ? `rgb(${a.join(",")})` : d);
  let styled = false;
  function addStyles() {
    if (styled) return; styled = true;
    const font = document.createElement("link");
    font.rel = "stylesheet"; font.href = "https://fonts.googleapis.com/css2?family=Nunito:wght@500;600;700;800&display=swap";
    document.head.appendChild(font);
    const css = document.createElement("style");
    css.textContent = `
      .dmp-tile { container-type: inline-size; }
      .dmp-band { position: absolute; left: 0; right: 0; bottom: 0; pointer-events: none; }
      .dmp-ov { position: absolute; left: 0; right: 0; margin: 0; text-align: center; font-family: Nunito, system-ui, sans-serif; white-space: nowrap; pointer-events: none; }
      .dmp-ov > div { overflow: visible; }
      .dmp-story { top: 88.5%; transform: translateY(-50%); font-size: ${U(44)}cqw; line-height: ${(60 / 44).toFixed(4)}; font-weight: 600; color: rgb(70,70,70); }
      .dmp-ded { font-size: ${U(46)}cqw; line-height: ${(66 / 46).toFixed(4)}; font-weight: 600; }
      .dmp-sign { font-size: ${U(40)}cqw; font-weight: 500; }
      .dmp-custom { left: ${U(127)}%; right: ${U(127)}%; transform: translateY(-50%); white-space: normal; font-weight: 600; }
      .dmp-custom .dmp-gap { height: .72em; }
      .dmp-block { left: ${U(52)}%; right: ${U(52)}%; transform: translateY(-50%); white-space: normal; font-weight: 600; color: rgb(38,38,46); }
      .dmp-por { font-size: ${U(40)}cqw; font-weight: 600; }
      .dmp-canvas { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
      .dmp-pt { line-height: 1; }
      .dmp-name { font-size: ${U(72)}cqw; font-weight: 800; line-height: 1.2; }
      .dmp-plain { inset: 8%; display: flex; align-items: center; justify-content: center; white-space: pre-line; font-weight: 600; line-height: 1.4; }`;
    document.head.appendChild(css);
  }

  // The parent's own dedication: wrapped to the page, centred on the kit's dedication block, smaller type when it's long
  // (same steps as bookkit.py: 46, 42, 38, 34 px until it fits 560 px).
  // The final dedication (team spec of 12 Oct, render2 in dedbuild.py): every line and the sign-off in one block, Nunito
  // SemiBold 63 px on 83 px lines, near-black, centred on the book's block_center_y; the parent's own words replace the
  // default lines (paragraphs kept), smaller type when it would need more than 6 lines of 1150 px.
  function dedicationBlock(paras, st) {
    paras = paras.filter((p, i) => p || (i > 0 && paras[i - 1]));
    let size = 35;
    for (let sz = 63; sz >= 35; sz -= 4) {
      const perLine = Math.floor(1150 / (sz * 0.5));
      const rows = paras.reduce((n, p) => n + (p ? Math.ceil(p.length / perLine) : 0), 0);
      if (rows * sz * 83 / 63 <= 498) { size = sz; break; }
    }
    return `<div class="dmp-ov dmp-block" style="top:${U(st.ded_center_y)}%;font-size:${U(size)}cqw;line-height:${(83 / 63).toFixed(4)}">` +
      paras.map((p) => (p ? `<div>${esc(p)}</div>` : "")).join("") + "</div>";
  }

  function customDedication(text, st) {
    const paras = String(text).replace(/\r/g, "").split("\n").map((p) => p.trim());
    while (paras.length && !paras[paras.length - 1]) paras.pop();
    let size = 34;
    for (const sz of [46, 42, 38, 34]) {
      const perLine = Math.floor(1000 / (sz * 0.52));
      const rows = paras.reduce((n, p) => n + (p ? Math.ceil(p.length / perLine) : 0.5), 0);
      if (rows * sz * 66 / 46 <= 560) { size = sz; break; }
    }
    return `<div class="dmp-ov dmp-custom" style="top:${U((st.ded_y || 720) + 170)}%;font-size:${U(size)}cqw;line-height:${(66 / 46).toFixed(4)};color:${rgb(st.text, "#464e62")}">` +
      paras.map((p) => (p ? `<div>${esc(p)}</div>` : '<div class="dmp-gap"></div>')).join("") + "</div>";
  }

  // ---- the portrait page ("Created especially for"), drawn like the print builder (print/toolkit/bookkit.py: the book's
  // portrait ops from its kit). The drawn portrait is placed, scaled, keyed to the page colour or set in a circle with the
  // book's ring on top, so the circle size and the text below it match the printed page (team, 13 Oct).
  const loadImg = (url) => new Promise((ok, bad) => {
    const i = new Image(); i.crossOrigin = "anonymous"; i.onload = () => ok(i); i.onerror = bad; i.src = url;
  });
  const BASE = 1254;
  function at1254(img, crop) {
    const c = document.createElement("canvas"); c.width = BASE; c.height = BASE;
    c.getContext("2d").drawImage(img, 0, 0, BASE, BASE);
    if (!crop) return c;
    const [x0, y0, x1, y1] = crop.map(Math.round), o = document.createElement("canvas");
    o.width = x1 - x0; o.height = y1 - y0; o.getContext("2d").drawImage(c, x0, y0, o.width, o.height, 0, 0, o.width, o.height);
    return o;
  }
  // background keyed to the page colour (keyart): weight w = clamp((r0 - distance to ref) / r1); with flood only the
  // background touching the edge is keyed and the art goes over what is already on the page
  function keyart(ctx, src, o) {
    const c = at1254(src, o.crop || [0, 0, BASE, BASE]), cx = c.getContext("2d"), W = c.width, H = c.height;
    const d = cx.getImageData(0, 0, W, H), a = d.data;
    let ref = o.ref;
    if (!ref) {
      ref = [0, 0, 0]; let n = 0;
      for (let y = 20; y < 60; y++) for (let x = 20; x < 60; x++) { const i = (y * W + x) * 4; ref[0] += a[i]; ref[1] += a[i + 1]; ref[2] += a[i + 2]; n++; }
      ref = ref.map((v) => v / n);
    }
    const dist = new Float32Array(W * H);
    for (let i = 0, j = 0; j < W * H; i += 4, j++) {
      dist[j] = Math.hypot(a[i] - ref[0], a[i + 1] - ref[1], a[i + 2] - ref[2]);
    }
    let reach = null;
    if (o.flood) {   // border-connected part of (dist < r0)
      reach = new Uint8Array(W * H); const st = [];
      const push = (j) => { if (!reach[j] && dist[j] < o.r0) { reach[j] = 1; st.push(j); } };
      for (let x = 0; x < W; x++) { push(x); push((H - 1) * W + x); }
      for (let y = 0; y < H; y++) { push(y * W); push(y * W + W - 1); }
      while (st.length) {
        const j = st.pop(), x = j % W;
        if (x > 0) push(j - 1); if (x < W - 1) push(j + 1); if (j >= W) push(j - W); if (j < W * (H - 1)) push(j + W);
      }
    }
    const to = o.to || [255, 255, 255], art = new Uint8Array(W * H);   // art: not background (for fit_bottom)
    for (let i = 0, j = 0; j < W * H; i += 4, j++) {
      let w = Math.min(1, Math.max(0, (o.r0 - dist[j]) / o.r1));
      art[j] = (reach ? w * reach[j] : w) < 0.5 ? 1 : 0;
      if (reach) { a[i + 3] = Math.round((1 - w * reach[j]) * 255); continue; }
      a[i] = a[i] * (1 - w) + to[0] * w; a[i + 1] = a[i + 1] * (1 - w) + to[1] * w; a[i + 2] = a[i + 2] * (1 - w) + to[2] * w;
    }
    cx.putImageData(d, 0, 0);
    let sc = o.scale || 1, y = o.y;
    if (o.fit_bottom != null) {
      // the drawn portrait never reaches into the text below it (bookkit.py fit_bottom; team, 16 Oct: the drawn dinosaur
      // wreath came out bigger and "Speciaal gemaakt voor" sat on the ferns): smaller, still centred, if it would
      let r0 = -1, r1 = -1;
      for (let yy = 0; yy < H; yy++) {
        let n = 0;
        for (let xx = 0; xx < W; xx++) n += art[yy * W + xx];
        if (n > 3) { if (r0 < 0) r0 = yy; r1 = yy; }
      }
      const bottom = y + (r1 + 1) * sc;
      if (r1 >= 0 && bottom > o.fit_bottom) {
        const top = y + r0 * sc, f = (o.fit_bottom - top) / (bottom - top);
        y = top - r0 * sc * f; sc *= f;
      }
    }
    const w = W * sc, h = H * sc, x = o.x != null ? o.x + (W * (o.scale || 1) - w) / 2 : (BASE - w) / 2;
    ctx.drawImage(c, x, y, w, h);
  }
  async function paintPortrait(canvas, job) {
    const ctx = canvas.getContext("2d");
    const urls = [...new Set(job.ops.map((o) => o.src === "page" ? job.art : o.url).filter(Boolean))];
    const imgs = {}; await Promise.all(urls.map(async (u) => { imgs[u] = await loadImg(u); }));
    const src = (o) => imgs[o.src === "page" ? job.art : o.url];
    ctx.fillStyle = rgb(job.bg, "#fff"); ctx.fillRect(0, 0, BASE, BASE);
    for (const o of job.ops) {
      if (o.op === "fill") { ctx.fillStyle = rgb(o.color, "#fff"); ctx.fillRect(0, 0, BASE, BASE); }
      else if (o.op === "art" || o.op === "overlay") ctx.drawImage(src(o), 0, 0, BASE, BASE);
      else if (o.op === "paste") {
        const c = at1254(src(o)); const px = c.getContext("2d").getImageData(o.fill_from_pixel[0], o.fill_from_pixel[1], 1, 1).data;
        ctx.fillStyle = `rgb(${px[0]},${px[1]},${px[2]})`; ctx.fillRect(0, 0, BASE, BASE);
        const [x0, y0, x1, y1] = o.crop; ctx.drawImage(c, x0, y0, x1 - x0, y1 - y0, o.at[0], o.at[1], x1 - x0, y1 - y0);
      } else if (o.op === "keyart") keyart(ctx, src(o), o);
      else if (o.op === "circle_art") {
        const e = o.edge ?? 6; ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(o.cx, o.cy, o.r + e, 0, 2 * Math.PI); ctx.fill();
        ctx.save(); ctx.beginPath(); ctx.arc(o.cx, o.cy, o.r, 0, 2 * Math.PI); ctx.clip();
        ctx.drawImage(src(o), o.cx - o.r, o.cy - o.r, 2 * o.r, 2 * o.r); ctx.restore();
      } else if (o.op === "thumb") thumb(ctx, src(o), o);
    }
  }
  // a find-it circle: a crop of the child's page (box on the 1254 px page) in the book's ring (bookkit.py "thumb": a filled
  // ring around the crop, or with outline the ring drawn on the crop's edge; a rounded rectangle when radius)
  function thumb(ctx, img, o) {
    const s = img.width / BASE, [b0, b1, b2, b3] = o.box, ring = rgb(o.ring, "#c8505a");
    const shape = (x, y, w, h, r) => { ctx.beginPath(); if (r == null) ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, 2 * Math.PI); else ctx.roundRect(x, y, w, h, r); };
    const inset = o.outline ? 0 : o.rw, x = o.x + inset, y = o.y + inset, w = o.w - 2 * inset, h = o.h - 2 * inset;
    if (!o.outline) { ctx.fillStyle = ring; shape(o.x, o.y, o.w, o.h, o.radius == null ? null : o.radius + o.rw); ctx.fill(); }
    ctx.save(); shape(x, y, w, h, o.radius); ctx.clip();
    ctx.drawImage(img, b0 * s, b1 * s, (b2 - b0) * s, (b3 - b1) * s, x, y, w, h); ctx.restore();
    if (o.outline) { ctx.strokeStyle = ring; ctx.lineWidth = o.rw; shape(x + o.rw / 2, y + o.rw / 2, w - o.rw, h - o.rw, o.radius); ctx.stroke(); }
  }
  const portraitJobs = new Map();
  function paintPending() {
    document.querySelectorAll("canvas[data-dmp-portrait]:not([data-painted])").forEach((c) => {
      const job = portraitJobs.get(c.dataset.dmpPortrait); if (!job) return;
      c.dataset.painted = "1";
      paintPortrait(c, job).catch(() => {   // images without CORS or a failed load: show the drawn page as it is
        const img = document.createElement("img"); img.src = job.art; img.alt = ""; c.replaceWith(img);
      });
    });
  }
  const WEIGHT = { Medium: 500, SemiBold: 600, Bold: 700, ExtraBold: 800 };

  function tileHtml(p, res, alt) {
    addStyles();
    const st = (res && res.style) || {};
    const key = esc(p.key || `${p.kind}-${p.page_number}`);
    if (p.kind === "pending") return `<li class="dm-page is-pending" data-key="${key}"><span class="dm-page__drawing">Drawing‚Ä¶</span></li>`;
    const img = p.url ? `<img src="${esc(p.url)}" alt="${esc(alt || "")}" draggable="false">` : "";
    let ov = "";
    if (p.layout === "story" && p.lines && p.lines.length) {
      // the book's own text style (book.json text_style of the final files); the older default otherwise
      const t = st.story;
      // the print's text safe area (bookkit.py safe_w, team 18 Oct: long Dutch lines ran past the page edge): the lines are set
      // smaller, all together, until the widest fits 1116 of the 1254 px page
      const fit = t ? fitSize(p.lines, t.size_px, t.weight === "Medium" ? 500 : 600, SAFE_W) : 1;
      const look = t ? ` style="top:${U(t.center_y_px)}%;font-size:${U(t.size_px * fit)}cqw;line-height:${(t.line_height_px / t.size_px).toFixed(4)};` +
        `font-weight:${t.weight === "Medium" ? 500 : 600};color:${rgb(t.color_rgb, "rgb(70,70,70)")}"` : "";
      // the white strip behind the text is painted here, not left to the drawing (bookkit.py band_top; team, 16 Oct: a drawn
      // page that kept only a thin white strip had its first line over the picture): solid from 30 px above the first line,
      // fading in over the 70 px above that
      const ts = t || { center_y_px: 1110, size_px: 44, line_height_px: 60 };
      const top = ts.center_y_px - (p.lines.length - 1) / 2 * ts.line_height_px - ts.size_px * 0.6 - 30, from = top - 70;
      ov = `<div class="dmp-band" style="top:${U(from)}%;background:linear-gradient(rgba(255,255,255,0),#fff ${(70 / (BASE - from) * 100).toFixed(2)}%)"></div>` +
        `<div class="dmp-ov dmp-story"${look}>${p.lines.map((l) => `<div>${esc(l)}</div>`).join("")}</div>`;
    } else if (p.layout === "portrait" && p.portrait) {
      const key = esc(p.key || `portrait-${p.page_number}`);
      portraitJobs.set(p.key || `portrait-${p.page_number}`, { ...p.portrait, art: p.url });
      setTimeout(paintPending, 0);
      const name = res.name || "";
      const texts = (p.portrait.texts || []).map((t) => `<div class="dmp-ov dmp-pt" style="top:${U(t.y)}%;font-size:${U(t.size)}cqw;` +
        `font-weight:${WEIGHT[t.w] || 600};color:${rgb(t.fill, "#464e62")}">${esc(String(t.t).replace(/\{N(ame)?\}/g, name))}</div>`).join("");
      return `<li class="dm-page dmp-tile" data-key="${key}"><canvas class="dmp-canvas" width="1254" height="1254" data-dmp-portrait="${key}"></canvas>${texts}</li>`;
    } else if (p.layout === "findit" && p.findit) {
      // the find-it page with the circles from the child's own drawn pages painted over the sample art
      const k = p.key || `findit-${p.page_number}`;
      portraitJobs.set(k, { ...p.findit, art: p.url });
      setTimeout(paintPending, 0);
      return `<li class="dm-page dmp-tile" data-key="${esc(k)}"><canvas class="dmp-canvas" width="1254" height="1254" data-dmp-portrait="${esc(k)}"></canvas></li>`;
    } else if (p.layout === "portrait") {
      ov = `<div class="dmp-ov dmp-por" style="top:${U(1010)}%;color:${rgb(st.text, "#464e62")}">Created especially for</div>` +
        `<div class="dmp-ov dmp-name" style="top:${U(1058)}%;color:${rgb(st.accent, "#c8505a")}">${esc(res.name || "")}</div>` +
        (p.wish ? `<div class="dmp-ov dmp-por" style="top:${U(1160)}%;color:rgb(105,105,110)">${esc(p.wish)}</div>` : "");
    } else if (p.layout === "dedication" && st.ded_center_y) {
      const words = p.custom ? String(p.text).replace(/\r/g, "").split("\n").map((l) => l.trim()) : (p.lines || []);
      ov = dedicationBlock(p.signoff ? words.concat([p.signoff]) : words, st);
    } else if (p.layout === "dedication" && p.custom) {
      ov = customDedication(p.text, st);
    } else if (p.layout === "dedication") {
      const y = st.ded_y || 720;
      ov = `<div class="dmp-ov dmp-ded" style="top:${U(y)}%;color:${rgb(st.text, "#464e62")}">${(p.lines || []).map((l) => `<div>${esc(l)}</div>`).join("")}</div>` +
        (p.signoff ? `<div class="dmp-ov dmp-sign" style="top:${U(y + 4 * 66 + 26)}%;color:${rgb(st.accent, "#c8505a")}">${esc(p.signoff)}</div>` : "");
    } else if (p.kind === "text") {
      // a text page without a print layout: the words on the book's background colour
      const len = String(p.text || "").length;
      ov = `<div class="dmp-ov dmp-plain" style="font-size:${U(len > 220 ? 34 : len > 140 ? 40 : 46)}cqw;color:${rgb(st.text, "#464e62")}">${esc(p.text)}</div>`;
    }
    const bg = !p.url || p.kind === "text" ? ` style="background:${rgb(st.bg, "#fbf4e8")}"` : "";
    return `<li class="dm-page dmp-tile" data-key="${key}"${bg}>${p.layout === "dedication" || p.kind === "art" ? img : ""}${ov}</li>`;
  }

  // Updates container to html without touching elements whose data-key is unchanged (a page already shown keeps its image).
  function morph(container, html) {
    const t = document.createElement("template");
    t.innerHTML = html;
    patch(container, t.content);
  }
  function patch(parent, next) {
    const olds = Array.from(parent.childNodes), news = Array.from(next.childNodes);
    news.forEach((n, i) => {
      const o = olds[i];
      if (!o) { parent.appendChild(n); return; }
      const ok = n.dataset && n.dataset.key, ook = o.dataset && o.dataset.key;
      if (o.nodeType !== n.nodeType || o.nodeName !== n.nodeName || ok !== ook) { parent.replaceChild(n, o); return; }
      if (o.nodeType === 3) { if (o.textContent !== n.textContent) o.textContent = n.textContent; return; }
      if (o.nodeType !== 1 || (ok && ok === ook)) return;
      Array.from(o.attributes).forEach((a) => { if (!n.hasAttribute(a.name)) o.removeAttribute(a.name); });
      Array.from(n.attributes).forEach((a) => { if (o.getAttribute(a.name) !== a.value) o.setAttribute(a.name, a.value); });
      patch(o, n);
    });
    for (let i = olds.length - 1; i >= news.length; i--) parent.removeChild(olds[i]);
  }

  // What the preview is doing now, for under the progress bar.
  function stageText(res, name) {
    if (!res || res.status !== "running") return "";
    const p = res.progress || {};
    if (!p.sheet_done) return `Sketching ${name || "your child"}`;
    if (p.done < p.total) return `Painting page ${Math.min(p.done + 1, p.total)} of ${p.total}`;
    return "Adding the finishing touches";
  }

  // Adds every book in one go: [{ book, variantId, quantity?, properties? }]. The drafts are kept (Birk, 18 Oct 2026): someone
  // who comes back from checkout without paying finds the same book, photo and preview again. Once the order is paid the
  // book is no longer a draft, so createBook starts a new one on the next visit.
  async function addBooksToCart(lines) {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: lines.map((l) => l.book.cartLine(l.variantId, l.quantity || 1, l.properties || {})) }),
    });
    if (!res.ok) throw new Error("Could not add to cart");
    return res.json();
  }

  // ---- one book on a product page (older form) ----
  let current = null;
  async function startDraft(handle, cover = "softcover") {
    current = await createBook({ handle, cover, key: handle });
    const { book } = await call(`/books/${current.id}`, current.token);
    return book;
  }
  const need = () => { if (!current) throw new Error("call startDraft first"); return current; };
  const update = (fields) => need().update(fields);
  const uploadPhoto = (file, opts) => need().uploadPhoto(file, opts);
  const preview = (email, consent = false, onProgress) => need().preview({ email, consent, onProgress });
  const getPreview = () => need().getPreview();
  async function addToCart(variantId, quantity = 1) {
    return addBooksToCart([{ book: need(), variantId, quantity }]);
  }

  async function joinList(email, { consent = false, source = null, utm = null } = {}) {
    if (!utm) {
      const p = new URLSearchParams(location.search);
      utm = Object.fromEntries([...p].filter(([k]) => k.startsWith("utm_")));
    }
    const res = await fetch(`${API}/leads`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, consent, source, utm }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error === "invalid_email" ? "Please enter a valid email" : "Sign-up failed");
    }
  }

  async function subscribe(email, { consent = false, source = "preview" } = {}) {
    await joinList(email, { consent, source }).catch(() => {});
    // Every email becomes a Shopify customer (13 Oct 2026). Email marketing is switched on when consent is true;
    // without it the person is still created, but with marketing off.
    const f = new FormData();
    f.append("form_type", "customer");
    f.append("utf8", "\u2713");
    f.append("contact[email]", email);
    f.append("contact[tags]", consent ? `newsletter,${source}` : source);
    if (!consent) f.append("contact[accepts_marketing]", "false");
    await fetch("/contact#dm-subscribe", { method: "POST", body: f, credentials: "same-origin" }).catch(() => {});
  }

  // A book's fixed pages (dedication art, colours), the same for every child: shown before anything is drawn.
  const layouts = {};
  function pageLayout(handle) {
    return layouts[handle] || (layouts[handle] = fetch(`${API}/layout?handle=${encodeURIComponent(handle)}`)
      .then((r) => (r.ok ? r.json() : null)).catch(() => null));
  }

  // The "my books" page (/pages/my-books?book=<token>, the link in the "Book Ready" email):
  //   getBook(token)    -> { id, handle, title, childName, childGender, pages: [{ page, url }] (cover first), changeDeadline,
  //                         approved, pdfUrl, status }
  //   approveBook(token) -> the parent approves the book: the change period ends; it prints once the team has approved it
  async function getBook(token) {
    const book = (await call("/books/by-token", token)).book;
    // pages as plain image links, cover first, as the "my books" page expects (audit, 18 Oct: it stored the page object as the
    // cover link, so the card showed the product photo instead of the child's cover)
    if (book && Array.isArray(book.pages)) book.pages = book.pages.map((p) => (typeof p === "string" ? p : p && p.url)).filter(Boolean);
    return book;
  }
  async function approveBook(token) { return call("/books/by-token/approve", token, { method: "POST", body: "{}" }); }

  window.DotMoon = { getBook, approveBook, createBook, addBooksToCart, percent, remainingMs, tileHtml, morph, stageText, subscribe, pageLayout, startDraft, update, uploadPhoto, preview, getPreview, addToCart, joinList };
})();

